import { onActivated, onBeforeUnmount, onDeactivated, shallowRef, watch, type Ref } from 'vue'
import {
  DISTRIBUTE_MEDIA_INTERSECTION_THRESHOLDS,
  DISTRIBUTE_MEDIA_PAUSE_THRESHOLD,
  DISTRIBUTE_MEDIA_PLAY_THRESHOLD,
} from '@/constants/distribute'

interface UseMediaLifecycleOptions {
  autoPlayWhenVisible?: boolean | Ref<boolean>
  endTime?: Ref<number | null>
  loopSegment?: boolean
  muted?: Ref<boolean>
  onTimeUpdate?: (currentTime: number) => void
  pauseThreshold?: number
  playThreshold?: number
  restartOnPlay?: boolean
  startTime?: Ref<number | null>
  visibilityTarget?: Ref<Element | null>
}

interface ObservedMediaState {
  handleVisibilityChange: (entry: IntersectionObserverEntry) => void
}

const observedElements = new WeakMap<Element, ObservedMediaState>()
let visibilityObserver: IntersectionObserver | null = null
let activeAudibleMedia: HTMLMediaElement | null = null

function isFiniteTime(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function releaseAudibleMedia(media: HTMLMediaElement) {
  if (activeAudibleMedia === media) {
    activeAudibleMedia = null
  }
}

function mutePreviousAudibleMedia(media: HTMLMediaElement) {
  if (!activeAudibleMedia || activeAudibleMedia === media) {
    return
  }

  activeAudibleMedia.muted = true
  activeAudibleMedia = null
}

function pauseAndReset(media: HTMLMediaElement | null) {
  if (!media) {
    return
  }

  releaseAudibleMedia(media)
  media.pause()
  media.currentTime = 0
}

function ensureObserver() {
  if (visibilityObserver || typeof window === 'undefined') {
    return
  }

  visibilityObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        observedElements.get(entry.target)?.handleVisibilityChange(entry)
      })
    },
    {
      threshold: Array.from(DISTRIBUTE_MEDIA_INTERSECTION_THRESHOLDS),
    },
  )
}

export function useMediaLifecycle(
  elementRef: Ref<HTMLMediaElement | null>,
  options: UseMediaLifecycleOptions = {},
) {
  const isPlaying = shallowRef(false)
  const isVisible = shallowRef(false)
  const isHovering = shallowRef(false)
  const playThreshold = options.playThreshold ?? DISTRIBUTE_MEDIA_PLAY_THRESHOLD
  const pauseThreshold = options.pauseThreshold ?? DISTRIBUTE_MEDIA_PAUSE_THRESHOLD
  let boundMedia: HTMLMediaElement | null = null
  let boundVisibilityTarget: Element | null = null
  let isLifecycleActive = true

  function resolveAutoPlayWhenVisible() {
    if (typeof options.autoPlayWhenVisible === 'boolean') {
      return options.autoPlayWhenVisible
    }

    return Boolean(options.autoPlayWhenVisible?.value)
  }

  function resolveMutedPreference() {
    if (resolveAutoPlayWhenVisible() && !isHovering.value) {
      return true
    }

    if (typeof options.muted?.value === 'boolean') {
      return options.muted.value
    }

    return false
  }

  function getSegmentStart() {
    const value = options.startTime?.value

    return isFiniteTime(value) ? value : null
  }

  function getSegmentEnd() {
    const value = options.endTime?.value

    return isFiniteTime(value) ? value : null
  }

  function seekToSegmentStart(media: HTMLMediaElement, force = false) {
    const startTime = getSegmentStart()
    const endTime = getSegmentEnd()

    if (startTime === null) {
      return
    }

    if (
      !force &&
      media.currentTime >= startTime &&
      (endTime === null || media.currentTime < endTime)
    ) {
      return
    }

    try {
      media.currentTime = startTime
      options.onTimeUpdate?.(startTime)
    } catch {
      // Some browsers reject seeking before metadata is available; the next loadedmetadata will retry.
    }
  }

  function pauseAtSegmentStart(media: HTMLMediaElement) {
    const startTime = getSegmentStart()

    media.pause()
    releaseAudibleMedia(media)
    isPlaying.value = false

    if (startTime !== null) {
      try {
        media.currentTime = startTime
      } catch {
        // Ignore seek errors on transient media state.
      }
      options.onTimeUpdate?.(startTime)
    }
  }

  function loopToSegmentStart(media: HTMLMediaElement) {
    const startTime = getSegmentStart()

    if (startTime === null) {
      media.currentTime = 0
      options.onTimeUpdate?.(0)
      return
    }

    try {
      media.currentTime = startTime
    } catch {
      // Ignore seek errors on transient media state.
    }

    options.onTimeUpdate?.(startTime)
  }

  function handleMediaTimeUpdate() {
    const media = boundMedia

    if (!media) {
      return
    }

    const endTime = getSegmentEnd()

    if (endTime !== null && media.currentTime >= endTime) {
      if (options.loopSegment) {
        loopToSegmentStart(media)
        return
      }

      pauseAtSegmentStart(media)
      return
    }

    options.onTimeUpdate?.(media.currentTime)
  }

  function handleLoadedMetadata() {
    const media = boundMedia

    if (media) {
      seekToSegmentStart(media)
    }
  }

  function applyMutedPreference(media: HTMLMediaElement) {
    if (resolveMutedPreference()) {
      media.muted = true
      releaseAudibleMedia(media)
      return
    }

    mutePreviousAudibleMedia(media)
    activeAudibleMedia = media
    media.muted = false
  }

  async function playMedia(media: HTMLMediaElement) {
    seekToSegmentStart(media)

    const shouldPlayMuted = resolveMutedPreference()
    media.muted = true
    releaseAudibleMedia(media)

    try {
      await media.play()

      if (elementRef.value !== media) {
        pauseAndReset(media)
        return
      }

      if (!shouldPlayMuted) {
        mutePreviousAudibleMedia(media)
        activeAudibleMedia = media
        media.muted = false
      }

      isPlaying.value = true
    } catch {
      isPlaying.value = false
    }
  }

  async function play() {
    isHovering.value = true

    const media = elementRef.value

    if (!media) {
      return
    }

    seekToSegmentStart(media, options.restartOnPlay)
    applyMutedPreference(media)
    await playMedia(media)
  }

  function stop() {
    const media = elementRef.value

    isHovering.value = false

    if (!media) {
      return
    }

    if (resolveAutoPlayWhenVisible() && isVisible.value) {
      media.muted = true
      releaseAudibleMedia(media)
      return
    }

    pauseAndReset(media)
    isPlaying.value = false
  }

  function pauseCurrentMedia(media: HTMLMediaElement) {
    pauseAndReset(media)
    isPlaying.value = false
  }

  function handleVisibilityChange(entry: IntersectionObserverEntry) {
    const media = elementRef.value
    const shouldPause = !entry.isIntersecting || entry.intersectionRatio <= pauseThreshold
    const shouldPlay = entry.isIntersecting && entry.intersectionRatio >= playThreshold

    if (!media || boundMedia !== media) {
      return
    }

    isVisible.value = !shouldPause

    if (shouldPause) {
      pauseCurrentMedia(media)
      return
    }

    if (resolveAutoPlayWhenVisible() && shouldPlay) {
      if (isHovering.value) {
        applyMutedPreference(media)
      } else {
        media.muted = true
        releaseAudibleMedia(media)
      }

      seekToSegmentStart(media)
      void playMedia(media)
    }
  }

  function resolveVisibilityTarget() {
    return options.visibilityTarget?.value ?? elementRef.value
  }

  function teardownVisibilityTarget() {
    if (!boundVisibilityTarget) {
      return
    }

    visibilityObserver?.unobserve(boundVisibilityTarget)
    observedElements.delete(boundVisibilityTarget)
    boundVisibilityTarget = null
    isVisible.value = false
  }

  function bindVisibilityTarget() {
    teardownVisibilityTarget()

    if (!boundMedia) {
      return
    }

    const target = resolveVisibilityTarget()

    if (!target) {
      return
    }

    observedElements.set(target, { handleVisibilityChange })
    visibilityObserver?.observe(target)
    boundVisibilityTarget = target
  }

  function bindMedia(media: HTMLMediaElement) {
    if (!isLifecycleActive) {
      return
    }

    if (boundMedia === media) {
      return
    }

    teardownMedia()
    ensureObserver()
    applyMutedPreference(media)
    media.addEventListener('loadedmetadata', handleLoadedMetadata)
    media.addEventListener('timeupdate', handleMediaTimeUpdate)
    boundMedia = media
    bindVisibilityTarget()

    if (isHovering.value) {
      void playMedia(media)
    }
  }

  function teardownMedia(media = boundMedia) {
    if (!media) {
      return
    }

    pauseCurrentMedia(media)
    media.removeEventListener('loadedmetadata', handleLoadedMetadata)
    media.removeEventListener('timeupdate', handleMediaTimeUpdate)
    teardownVisibilityTarget()

    if (boundMedia === media) {
      boundMedia = null
    }
  }

  watch(
    elementRef,
    (media) => {
      if (!isLifecycleActive) {
        teardownMedia()
        return
      }

      if (media) {
        bindMedia(media)
        return
      }

      teardownMedia()
    },
    { immediate: true, flush: 'post' },
  )

  watch(
    () => options.visibilityTarget?.value,
    () => {
      if (boundMedia) {
        bindVisibilityTarget()
      }
    },
    { flush: 'post' },
  )

  watch(
    resolveAutoPlayWhenVisible,
    (enabled) => {
      const media = elementRef.value

      if (!media) {
        return
      }

      if (!enabled) {
        if (!isHovering.value) {
          pauseCurrentMedia(media)
        }
        return
      }

      if (isVisible.value && !isHovering.value) {
        media.muted = true
        releaseAudibleMedia(media)
        void playMedia(media)
      }
    },
    { flush: 'post' },
  )

  watch(
    () => options.muted?.value,
    (muted) => {
      if (typeof muted !== 'boolean' || !elementRef.value) {
        return
      }

      applyMutedPreference(elementRef.value)
    },
  )

  watch(
    () => [options.startTime?.value, options.endTime?.value],
    () => {
      if (elementRef.value) {
        seekToSegmentStart(elementRef.value, true)
      }
    },
  )

  onBeforeUnmount(() => {
    isLifecycleActive = false
    teardownMedia()
  })

  onActivated(() => {
    isLifecycleActive = true

    if (elementRef.value) {
      bindMedia(elementRef.value)
    }
  })

  onDeactivated(() => {
    isLifecycleActive = false
    isHovering.value = false
    teardownMedia()
  })

  return {
    isPlaying,
    isVisible,
    play,
    stop,
  }
}

export function resetMediaLifecycleForTest() {
  visibilityObserver?.disconnect()
  visibilityObserver = null
  activeAudibleMedia = null
}
